import React from "react";
import { Phone, MapPin, Instagram, Twitter, ArrowLeft } from "lucide-react";

const TikTokIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.79 1.52V6.76a4.85 4.85 0 01-1.02-.07z"/>
  </svg>
);

const LinkedInIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/>
    <circle cx="4" cy="4" r="2"/>
  </svg>
);

const LOGO_DEFAULT = "https://customer-assets.emergentagent.com/job_elite-realty-40/artifacts/q2vcqxyz_%D8%AA%D8%B5%D9%85%D9%8A%D9%85%20%D8%A8%D8%AF%D9%88%D9%86%20%D8%B9%D9%86%D9%88%D8%A7%D9%86%20-%202026-06-21T092601.962.png";

const navLinks = [
  { label: "الرئيسية", href: "#home" },
  { label: "خدماتنا", href: "#services" },
  { label: "مشاريعنا", href: "#projects" },
  { label: "عن الشركة", href: "#values" },
  { label: "تواصل معنا", href: "#contact" },
];

const services = [
  "التسويق العقاري", "الوساطة العقارية", "إدارة الأملاك",
  "العقود الإيجارية", "التطوير العقاري", "المزادات العقارية",
];

const Footer = ({ footer, contact, logo }) => {
  const LOGO = logo || LOGO_DEFAULT;
  const companyName = footer?.company_name || "الوسيم العقارية";
  const tagline = footer?.tagline || "Al Wasim Real Estate";
  const description = footer?.description || "شركة عقارية رائدة في المملكة العربية السعودية";
  const phone1 = footer?.phone1 || contact?.phone1 || "0555661937";
  const phone2 = footer?.phone2 || contact?.phone2 || "0553972038";
  const address = footer?.address || contact?.address || "جازان - طريق المدينة المنورة - حي المحمدية";
  const copyright = footer?.copyright || "جميع الحقوق محفوظة © 2025 الوسيم العقارية";

  // Read social from contact section (where admin saves them) with footer fallback
  const social_instagram = contact?.social_instagram || footer?.social_instagram || "";
  const social_twitter   = contact?.social_twitter   || footer?.social_twitter   || "";
  const social_tiktok    = contact?.social_tiktok    || footer?.social_tiktok    || "";
  const social_linkedin  = contact?.social_linkedin  || footer?.social_linkedin  || "";

  const socialLinks = [
    { href: social_instagram ? `https://instagram.com/${social_instagram.replace("@","")}` : "#", Icon: Instagram, label: "انستقرام", show: !!social_instagram },
    { href: social_twitter ? `https://twitter.com/${social_twitter.replace("@","")}` : "#", Icon: Twitter, label: "تويتر", show: !!social_twitter },
    { href: social_tiktok ? `https://tiktok.com/@${social_tiktok.replace("@","")}` : "#", Icon: TikTokIcon, label: "تيك توك", show: !!social_tiktok },
    { href: social_linkedin ? `https://linkedin.com/company/${social_linkedin.replace("@","")}` : "#", Icon: LinkedInIcon, label: "لينكدإن", show: !!social_linkedin },
  ];

  return (
    <footer data-testid="footer" className="bg-[#0A1128] text-white pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Gold top line */}
        <div className="h-px bg-gradient-to-r from-transparent via-[#ecc775] to-transparent mb-16"></div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-16">
          {/* Brand */}
          <div className="lg:col-span-1">
            <img src={LOGO} alt={companyName} className="h-16 w-auto object-contain mb-4" style={{ mixBlendMode: "screen" }} />
            <p className="text-[#ecc775] font-bold text-sm mb-3">{tagline}</p>
            <p className="text-white/60 text-sm leading-relaxed mb-6">{description}</p>
            <div className="flex gap-3 flex-wrap">
              {socialLinks.map(({ href, Icon, label, show }) => show && (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" title={label}
                  className="w-10 h-10 bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:bg-[#ecc775] hover:text-[#173269] hover:border-[#ecc775] transition-all duration-300">
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-[#ecc775] font-bold text-base mb-6 flex items-center gap-2">
              <span className="w-8 h-0.5 bg-[#ecc775]"></span>
              خدماتنا
            </h4>
            <ul className="space-y-3">
              {services.map((s) => (
                <li key={s}>
                  <a href="#services" className="text-white/60 text-sm hover:text-[#ecc775] transition-colors flex items-center gap-2 group">
                    <ArrowLeft size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    {s}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-[#ecc775] font-bold text-base mb-6 flex items-center gap-2">
              <span className="w-8 h-0.5 bg-[#ecc775]"></span>
              روابط سريعة
            </h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <a href={link.href} className="text-white/60 text-sm hover:text-[#ecc775] transition-colors flex items-center gap-2 group">
                    <ArrowLeft size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-[#ecc775] font-bold text-base mb-6 flex items-center gap-2">
              <span className="w-8 h-0.5 bg-[#ecc775]"></span>
              تواصل معنا
            </h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Phone size={16} className="text-[#ecc775] mt-0.5 flex-shrink-0" />
                <div>
                  <a href={`tel:${phone1}`} className="text-white/70 text-sm hover:text-[#ecc775] transition-colors block" dir="ltr">{phone1}</a>
                  <a href={`tel:${phone2}`} className="text-white/70 text-sm hover:text-[#ecc775] transition-colors block" dir="ltr">{phone2}</a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin size={16} className="text-[#ecc775] mt-0.5 flex-shrink-0" />
                <p className="text-white/70 text-sm leading-relaxed">{address}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="h-px bg-white/10 mb-8"></div>
        <div className="flex flex-col md:flex-row items-center justify-center gap-4">
          <p className="text-white/40 text-sm">{copyright}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
